/**
 * ScoreCategoriesEditor Component
 *
 * Entity-specific editor for ScoreCategories lookup table
 * Shows associated scores count with View link
 * Uses modal dialog for add/edit operations
 */

import {
  useScoreCategories,
  useCreateScoreCategory,
  useUpdateScoreCategory,
  useDeleteScoreCategory,
  useAdminScores,
} from '@medsuite/api-client';
import {
  Button,
  Alert,
  Spinner,
  Modal,
  ModalFooter,
  Input,
  useDialog,
} from '@medsuite/ui-components';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ScoreCategory {
  score_category_id: number;
  score_category_name: string;
  selection_type: 'S' | 'M';
}

interface ScoreCategoriesEditorProps {
  onDataLoaded?: (count: number) => void;
}

export default function ScoreCategoriesEditor({ onDataLoaded }: ScoreCategoriesEditorProps) {
  const { data: scoreCategories = [], isLoading, error } = useScoreCategories();
  const { data: scores = [] } = useAdminScores();
  const createMutation = useCreateScoreCategory();
  const updateMutation = useUpdateScoreCategory();
  const deleteMutation = useDeleteScoreCategory();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ScoreCategory | null>(null);
  const [formData, setFormData] = useState({
    score_category_name: '',
    selection_type: 'S' as 'S' | 'M',
  });

  // Dialog hooks
  const { alert, confirm } = useDialog();

  // Notify parent of data count
  useEffect(() => {
    if (onDataLoaded && scoreCategories.length > 0) {
      onDataLoaded(scoreCategories.length);
    }
  }, [scoreCategories.length, onDataLoaded]);

  // Count scores per category
  const scoreCountsByCategory = scores.reduce(
    (acc, score) => {
      acc[score.score_category_id] = (acc[score.score_category_id] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  );

  const handleOpenAddModal = () => {
    setEditingCategory(null);
    setFormData({ score_category_name: '', selection_type: 'S' });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (category: ScoreCategory) => {
    setEditingCategory(category);
    setFormData({
      score_category_name: category.score_category_name,
      selection_type: category.selection_type,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({ score_category_name: '', selection_type: 'S' });
  };

  const handleSave = async () => {
    try {
      if (editingCategory) {
        await updateMutation.mutateAsync({
          id: editingCategory.score_category_id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };

  const handleDelete = async (id: number) => {
    const scoreCount = scoreCountsByCategory[id] || 0;
    if (scoreCount > 0) {
      await alert({
        title: 'Cannot Delete',
        message: `Cannot delete this category because it has ${scoreCount} associated score(s). Please remove the scores first.`,
        variant: 'warning',
      });
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load score categories: {error.message}</Alert>;
  }

  const isFormValid = formData.score_category_name.trim().length > 0;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Add New Button */}
      <div className="flex items-center justify-end">
        <Button variant="primary" onClick={handleOpenAddModal}>
          Add New
        </Button>
      </div>

      {/* Categories Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Category Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Selection Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Associated Scores
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {scoreCategories.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No score categories found. Click &quot;Add New&quot; to create one.
                </td>
              </tr>
            ) : (
              scoreCategories.map((category) => {
                const scoreCount = scoreCountsByCategory[category.score_category_id] || 0;
                return (
                  <tr key={category.score_category_id}>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {category.score_category_id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {category.score_category_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        {category.selection_type === 'S' ? 'Single' : 'Multiple'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 dark:text-gray-100">
                          {scoreCount} {scoreCount === 1 ? 'score' : 'scores'}
                        </span>
                        <Link
                          to={`/admin/scores?category=${category.score_category_id}`}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleOpenEditModal(category)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(category.score_category_id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Edit Score Category' : 'Add Score Category'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category Name
            </label>
            <Input
              type="text"
              value={formData.score_category_name}
              onChange={(e) => setFormData({ ...formData, score_category_name: e.target.value })}
              placeholder="Enter category name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Selection Type
            </label>
            <select
              value={formData.selection_type}
              onChange={(e) =>
                setFormData({ ...formData, selection_type: e.target.value as 'S' | 'M' })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            >
              <option value="S">Single - User can select one score</option>
              <option value="M">Multiple - User can select multiple scores</option>
            </select>
          </div>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!isFormValid || isSaving}>
            {isSaving ? 'Saving...' : editingCategory ? 'Save Changes' : 'Add Category'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
